import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileUp } from "lucide-react";
import { toast } from "sonner";
import { api, download, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, TZ } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, StatusBadge } from "@/components/shared";
import { cn } from "@/lib/utils";

const selectCls = "h-9 w-full rounded-md bg-[#0f141f] border border-[#26334d] text-xs px-2 text-slate-100 font-mono";

export default function UberImport() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [payResult, setPayResult] = useState<any>(null);
  // Two different documents, chosen explicitly. The trips export is what reconciles against GPS;
  // the payments statement is what makes the money exact. Auto-detection still corrects a wrong pick.
  const [fileKind, setFileKind] = useState<"TRIPS" | "PAYMENTS">("TRIPS");
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [dateFormat, setDateFormat] = useState("auto");
  const [result, setResult] = useState<any>(null);
  const [platform, setPlatform] = useState("UBER");
  const imports = useQuery({ queryKey: ["imports"], queryFn: async () => (await api.get("/uber/imports")).data });
  const trips = useQuery({ queryKey: ["trips"], queryFn: async () => (await api.get("/uber/trips", { params: { hours: 48, limit: 100 } })).data });

  const doPreview = useMutation({
    mutationFn: async (f: File) => { const fd = new FormData(); fd.append("file", f); fd.append("platform", platform); return (await api.post("/uber/import/preview", fd)).data; },
    onSuccess: (d) => { setPreview(d); setMapping(d.suggestedMapping || {}); setDateFormat(d.dateFormat || "auto"); setResult(null); setPayResult(null); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const importPayments = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file!);
      fd.append("platform", platform);
      return (await api.post("/uber/import-payments", fd)).data;
    },
    onSuccess: (d) => { setPayResult(d); qc.invalidateQueries(); toast.success(`Payments imported · real commission ${d.totals.effectiveCommissionPct}%`); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const doImport = useMutation({
    mutationFn: async () => { const fd = new FormData(); fd.append("file", file!); fd.append("mapping", JSON.stringify(mapping)); fd.append("dateFormat", dateFormat); fd.append("timezone", TZ); fd.append("platform", platform); return (await api.post("/uber/import", fd)).data; },
    onSuccess: (d) => { setResult(d); qc.invalidateQueries(); toast.success(`Imported ${d.imported} trips · ${d.duplicates} duplicates · ${d.rejected} rejected`); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pick = (f: File | undefined) => { if (!f) return; setFile(f); doPreview.mutate(f); };
  const required = ["externalTripId", "pickupTime", "dropoffTime"];

  return (
    <div data-testid="uber-import-page">
      <PageHeader title="Ride platform import" subtitle="Two different documents: a Trips export reconciles against the GPS journeys; a Payments statement carries the real platform commission and driver earnings. Pick which one you are uploading." actions={<>
        <div data-testid="filekind-toggle" className="flex rounded-md border border-[#26334d] overflow-hidden mr-2">
          {([["TRIPS", "Trips"], ["PAYMENTS", "Payments"]] as const).map(([k, l]) => (
            <button key={k} data-testid={`filekind-${k.toLowerCase()}-button`}
                    onClick={() => { setFileKind(k); setPreview(null); setResult(null); setPayResult(null); }}
                    className={cn("px-3 py-1.5 font-mono text-xs", fileKind === k ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:bg-[#182030]")}>{l}</button>
          ))}
        </div>
        <div data-testid="platform-toggle" className="flex rounded-md border border-[#26334d] overflow-hidden">{["UBER", "BOLT"].map((p) => <button key={p} data-testid={`platform-${p.toLowerCase()}-button`} onClick={() => { setPlatform(p); setPreview(null); setResult(null); }} className={cn("px-3 py-1.5 font-mono text-xs", platform === p ? (p === "UBER" ? "bg-slate-100 text-black" : "bg-emerald-500 text-black") : "text-slate-400 hover:bg-[#182030]")}>{p}</button>)}</div>
        <Button variant="outline" data-testid="download-sample-csv-button" onClick={() => download("/demo/uber-sample", "uber-fleet-sample.csv")} className="border-[#26334d] bg-transparent text-slate-200 hover:bg-[#182030]"><Download className="h-4 w-4 mr-1" /> Sample CSV (dupes)</Button>
        <Button variant="outline" data-testid="download-fresh-csv-button" onClick={() => download("/demo/uber-sample?fresh=1", "uber-fleet-fresh.csv")} className="border-[#26334d] bg-transparent text-slate-200 hover:bg-[#182030]"><Download className="h-4 w-4 mr-1" /> Fresh CSV (new trips)</Button></>} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4">
          <div data-testid="uber-csv-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }} onClick={() => fileRef.current?.click()}
            className={cn("tg-card border-dashed p-8 text-center cursor-pointer transition-colors hover:border-blue-500", !can("uber:import") && "opacity-50 pointer-events-none")}>
            <FileUp className="h-8 w-8 mx-auto text-blue-400 mb-2" />
            <div className="text-sm text-slate-200">{file ? file.name : "Drop the Uber export here or click to browse"}</div>
            <div className="text-xs text-slate-500 mt-1">.csv or .xlsx · standard columns auto-detected</div>
            <input ref={fileRef} data-testid="uber-csv-upload-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          </div>
          {preview && preview.kind !== fileKind && (
            <div className="tg-card px-4 py-2.5 font-mono text-[11px] text-amber-300/90" data-testid="filekind-mismatch">
              You chose <b>{fileKind === "TRIPS" ? "Trips" : "Payments"}</b> but this file looks like a{" "}
              <b>{preview.kind === "PAYMENTS" ? "payments statement" : "trips export"}</b>. Handling it as a{" "}
              {preview.kind === "PAYMENTS" ? "payments statement" : "trips export"}.
            </div>
          )}
          {preview?.kind === "PAYMENTS" && (
            <div className="tg-card p-5 space-y-3" data-testid="payments-preview">
              <div className="flex items-center gap-2">
                <div className="tg-label">Driver payments statement</div>
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">NOT TRIPS</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">{preview.note}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Drivers", preview.payments.totals.drivers],
                  ["Gross fares", preview.payments.totals.grossFare],
                  ["Platform fee", preview.payments.totals.serviceFee],
                  ["Real commission", `${preview.payments.totals.effectiveCommissionPct}%`]].map(([l, v]: any) => (
                  <div key={l} className="border border-[#26334d] rounded-md px-3 py-2">
                    <div className="tg-label text-[10px]">{l}</div>
                    <div className="font-mono text-sm text-slate-100">{v}</div>
                  </div>
                ))}
              </div>
              {preview.periodFrom && (
                <div className="font-mono text-[11px] text-slate-500">
                  Period {String(preview.periodFrom).slice(0, 10)} → {String(preview.periodTo).slice(0, 10)} (read from the filename)
                </div>
              )}
              {preview.payments.warnings?.map((w: string) => (
                <div key={w} className="font-mono text-[11px] text-amber-300/90">{w}</div>
              ))}
              <div className="overflow-x-auto">
                <table className="text-[11px] font-mono w-full">
                  <thead><tr className="text-slate-500 text-left">
                    {["Driver", "Gross", "Fee", "%", "Net paid", "Cash", "Tips"].map((h) => <th key={h} className="pr-3 pb-1">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {preview.payments.drivers.filter((d: any) => !d.settlementOnly).slice(0, 20).map((d: any) => (
                      <tr key={d.driverName} className="border-t border-[#26334d]">
                        <td className="pr-3 py-1 text-slate-200 whitespace-nowrap">{d.driverName}</td>
                        <td className="pr-3 text-slate-300">{d.grossFare}</td>
                        <td className="pr-3 text-slate-400">{d.serviceFee}</td>
                        <td className="pr-3 text-amber-300">{d.commissionPct}%</td>
                        <td className="pr-3 text-emerald-400">{d.netPaid}</td>
                        <td className="pr-3 text-slate-400">{d.cashCollected}</td>
                        <td className="pr-3 text-slate-400">{d.tips}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button data-testid="import-payments-button" disabled={importPayments.isPending}
                      onClick={() => importPayments.mutate()} className="bg-blue-600 hover:bg-blue-500 text-white">
                {importPayments.isPending ? "Importing…" : "Import payments"}
              </Button>
              {payResult && (
                <div className="font-mono text-[11px] text-emerald-400">{payResult.note}</div>
              )}
            </div>
          )}
          {preview && preview.kind !== "PAYMENTS" && (
            <div className="tg-card p-4 space-y-4 tg-enter" data-testid="column-mapping-panel">
              <div className="flex items-center justify-between"><div className="tg-label">Column mapping · {preview.rows} rows</div>
                <div className="flex items-center gap-2"><span className="tg-label">Date format</span><Input data-testid="date-format-input" className="h-8 w-40 bg-[#0f141f] border-[#26334d] font-mono text-xs" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} placeholder="auto or %d/%m/%Y %H:%M" /></div></div>
              <div className="grid grid-cols-2 gap-2">
                {preview.fields.map((f: string) => (
                  <div key={f} className="flex items-center gap-2">
                    <span className={cn("font-mono text-[11px] w-32 shrink-0", required.includes(f) ? "text-slate-100" : "text-slate-400")}>{f}{required.includes(f) && "*"}</span>
                    <select data-testid={`mapping-select-${f}`} className={selectCls} value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}>
                      <option value="">— not mapped —</option>{preview.headers.map((h: string) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto"><table className="text-[11px] font-mono w-full"><thead><tr>{preview.headers.map((h: string) => <th key={h} className="text-left text-slate-500 pr-3 pb-1 whitespace-nowrap">{h}</th>)}</tr></thead>
                <tbody>{preview.sample.map((r: any, i: number) => <tr key={i} className="border-t border-[#26334d]">{preview.headers.map((h: string) => <td key={h} className="pr-3 py-1 text-slate-300 whitespace-nowrap max-w-[160px] truncate">{r[h]}</td>)}</tr>)}</tbody></table></div>
              <Button data-testid="uber-import-commit-button" disabled={doImport.isPending || required.some((r) => !mapping[r])} onClick={() => doImport.mutate()} className="bg-blue-600 hover:bg-blue-500 text-white">{doImport.isPending ? "Importing & reconciling…" : "Import & reconcile"}</Button>
            </div>
          )}
          {result && (
            <div className="tg-card p-4 tg-enter" data-testid="import-result-panel">
              <div className="grid grid-cols-4 gap-3 text-center">
                {[["Rows", result.rows], ["Imported", result.imported, "text-emerald-400"], ["Duplicates", result.duplicates, "text-amber-400"], ["Rejected", result.rejected, "text-rose-400"]].map(([l, v, c]) => (
                  <div key={l as string}><div className={cn("font-display text-2xl", c as string)} data-testid={`import-result-${(l as string).toLowerCase()}`}>{v as number}</div><div className="tg-label">{l}</div></div>
                ))}
              </div>
              {result.errors?.length > 0 && <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">{result.errors.map((e: any, i: number) => <div key={i} className="font-mono text-[11px] text-rose-300">row {e.row}: {e.error}</div>)}</div>}
              <div className="mt-3 text-xs text-slate-400">Reconciliation: {Object.entries(result.reconciliation?.byStatus || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 space-y-4">
          <div className="tg-card p-4" data-testid="import-history">
            <div className="tg-label mb-2">Import batches</div>
            {(imports.data || []).length === 0 && <div className="text-xs text-slate-500">No imports yet. Demo trips were seeded directly.</div>}
            {(imports.data || []).map((b: any) => <div key={b.id} className="flex justify-between text-xs py-1.5 border-b border-[#26334d] last:border-0"><span className="text-slate-200 truncate">{b.filename}</span><span className="font-mono text-slate-400">{b.imported}✓ {b.duplicates}= {b.rejected}✗ · {fmtDateTime(b.createdAt)}</span></div>)}
          </div>
          <div className="tg-card p-4 max-h-[50vh] overflow-y-auto" data-testid="uber-trips-list">
            <div className="tg-label mb-2">Declared trips · last 48 h ({trips.data?.length || 0})</div>
            {(trips.data || []).map((t: any) => (
              <div key={t.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-[#26334d] last:border-0">
                <span className="font-mono text-slate-300 w-28 truncate">{t.externalTripId}</span>
                <span className={cn("font-mono text-[9px] rounded px-1", t.platform === "BOLT" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-300")}>{t.platform || "UBER"}</span>
                <span className="text-slate-400 flex-1 truncate">{t.vehiclePlate} · {t.driverName}</span>
                <span className="font-mono text-slate-500">{fmtDateTime(t.pickupTime)}</span>
                <StatusBadge status={t.reconciliation?.status || t.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
